'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { motion, type Variants } from 'framer-motion';
import { ThemeSwitcher } from '../ui/design-system';
import type { ProductGroup } from '@/lib/data/products';
import { legalContactData, LegalService, type LegalPage } from '@/lib/data/legal';

const footerVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.6, 
      ease: [0.22, 1, 0.36, 1],
      staggerChildren: 0.1,
      delayChildren: 0.2
    } 
  }
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
  }
};

interface FooterProps {
  productGroups?: ProductGroup[];
}

// Collapsible section component for mobile view
const CollapsibleSection = ({ 
  title, 
  children,
  isLast = false
}: { 
  title: string; 
  children: React.ReactNode;
  isLast?: boolean;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={`${!isLast ? 'border-b border-gray-100 dark:border-gray-800/30' : ''} py-4 md:border-none md:py-0`}>
      {/* For desktop, keep the same design */}
      <div className="hidden md:block">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
          {title}
        </h3>
        {children}
      </div>
      
      {/* For mobile, show always expanded content */}
      <div className="md:hidden">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
          {title}
        </h3>
        <div className="mt-4">
          {children}
        </div>
      </div>
    </div>
  );
};

export function Footer({
  productGroups = [],
}: FooterProps) {
  const { 
    companyName, 
    address: factoryAddress, 
    email, 
    phone 
  } = legalContactData;
  const [whatsappUrl, setWhatsappUrl] = useState("");
  const [legalPages, setLegalPages] = useState<LegalPage[]>([]);
  
  useEffect(() => {
    // Detect if device is mobile
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const cleanPhone = phone.replace(/\s+/g, '');
    const messageText = encodeURIComponent("👉 Hi PAVISHA Team! 👋\nCan you share your latest catalog and pricing? Also, let me know the MOQ and delivery options.\n\nLooking forward to your response. Thanks! 😊");
    
    // Set appropriate WhatsApp URL based on device
    if (isMobile) {
      // For mobile devices, use wa.me format which will open the app
      setWhatsappUrl(`https://wa.me/91${cleanPhone}?text=${messageText}`);
    } else {
      // For desktop/tablet, use web.whatsapp.com
      setWhatsappUrl(`https://web.whatsapp.com/send?phone=91${cleanPhone}&text=${messageText}`);
    }

    // Load legal pages from database
    const loadLegalPages = async () => {
      try {
        const pages = await LegalService.getAllLegalPages();
        setLegalPages(pages);
      } catch (error) {
        console.error('Failed to load legal pages:', error);
        // LegalService already handles fallback internally
      }
    };

    loadLegalPages();
  }, [phone]);
  
  return (
    <footer className="bg-white dark:bg-black border-t border-gray-100 dark:border-gray-800/30 pt-6 md:pt-8 lg:pt-10 pb-8 md:pb-6" itemScope itemType="https://schema.org/WPFooter">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-4 gap-4 md:gap-6 lg:gap-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={footerVariants}
        >
          {/* Company Information - Always visible on mobile */}
          <motion.div variants={itemVariants} className="col-span-1 md:col-span-1 py-2 md:py-0">
            <Link href="/" className="inline-block" aria-label="PAVISHA Home">
              <span className="text-xl md:text-xl font-bold premium-gradient-text">
                {companyName}
              </span>
            </Link>
            <div className="mt-3 space-y-2 text-xs md:text-sm text-gray-600 dark:text-gray-300" itemScope itemType="https://schema.org/Organization">
              <meta itemProp="name" content={companyName} />
              <div className="flex items-start">
                <svg className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <a 
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(factoryAddress)}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="break-words hover:text-primary-600 dark:hover:text-primary-400 premium-transition-all"
                  itemProp="address" 
                  itemScope 
                  itemType="https://schema.org/PostalAddress"
                >
                  <meta itemProp="streetAddress" content="Mahadeo Asthan, March Marchi Rd, near Bypass Thana, Paijawa" />
                  <meta itemProp="addressLocality" content="Patna" />
                  <meta itemProp="addressRegion" content="Bihar" />
                  <meta itemProp="postalCode" content="800007" />
                  <meta itemProp="addressCountry" content="IN" />
                  {factoryAddress}
                </a>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                <a href={`mailto:${email}`} className="hover:text-primary-600 dark:hover:text-primary-400 premium-transition-all break-words" itemProp="email">
                  {email}
                </a>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                </svg>
                <a href={`tel:+91${phone.replace(/\s+/g, '')}`} className="hover:text-primary-600 dark:hover:text-primary-400 premium-transition-all" itemProp="telephone">
                  +91 {phone}
                </a>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-5" itemScope itemType="https://schema.org/Organization">
              <link itemProp="url" href="https://pavisha.com" />
              <a 
                href="https://facebook.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all"
                aria-label="Facebook"
                itemProp="sameAs"
              >
                <svg className="h-5 w-5 md:h-5 md:w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path>
                </svg>
              </a>
              <a 
                href="https://instagram.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all"
                aria-label="Instagram"
              >
                <svg className="h-5 w-5 md:h-5 md:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8zm0-13a5 5 0 1 0 5 5 5 5 0 0 0-5-5zm0 8a3 3 0 1 1 3-3 3 3 0 0 1-3 3zm5-9a1.5 1.5 0 1 0 1.5 1.5A1.5 1.5 0 0 0 17 6z"></path>
                </svg>
              </a>
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all"
                aria-label="LinkedIn"
              >
                <svg className="h-5 w-5 md:h-5 md:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                </svg>
              </a>
              <a 
                href={whatsappUrl}
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all"
                aria-label="WhatsApp"
              >
                <svg className="h-5 w-5 md:h-5 md:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"></path>
                </svg>
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all"
                aria-label="X (Twitter)"
              >
                <svg className="h-5 w-5 md:h-5 md:w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
          </motion.div>

          {/* Replace mobile links section with consistent columns */}
          <motion.div variants={itemVariants} className="md:hidden col-span-1 mt-4 border-t border-gray-100 dark:border-gray-800/30 pt-4">
            <div className="grid grid-cols-3 gap-4">
              {/* Quick Links */}
              <div className="h-full">
                <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-3">
                  QUICK LINKS
                </h3>
                <ul className="space-y-3">
                  <li>
                    <Link href="/" className="text-xs text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                      Home
                    </Link>
                  </li>
                  <li>
                    <Link href="/about" className="text-xs text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                      About
                    </Link>
                  </li>
                  <li>
                    <Link href="/products" className="text-xs text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                      Products
                    </Link>
                  </li>
                  <li>
                    <Link href="/industries" className="text-xs text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                      Industries We Serve
                    </Link>
                  </li>
                  <li>
                    <Link href="/contact" className="text-xs text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                      Contact Us
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Products */}
              <div>
                <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-2">
                  PRODUCTS
                </h3>
                <ul className="space-y-2">
                  {productGroups.flatMap(group => group.categories).slice(0, 5).map(category => (
                    <li key={category.slug}>
                      <Link href={`/products/${category.slug}`} className="text-xs text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                        {category.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Legal */}
              <div>
                <h3 className="text-xs font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-2">
                  LEGAL
                </h3>
                <ul className="space-y-2">
                  {legalPages.map(page => (
                    <li key={page.id}>
                      <Link href={`/${page.slug}`} className="text-xs text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all" prefetch={true}>
                        {page.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Desktop view - Quick Links */}
          <motion.div variants={itemVariants} className="hidden md:block col-span-1">
            <CollapsibleSection title="Quick Links">
              <ul className="space-y-3 md:space-y-2">
                <li>
                  <Link href="/" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                    Home
                  </Link>
                </li>
                <li>
                  <Link href="/about" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                    About
                  </Link>
                </li>
                <li>
                  <Link href="/products" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                    Products
                  </Link>
                </li>
                <li>
                  <Link href="/industries" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                    Industries We Serve
                  </Link>
                </li>
                <li>
                  <Link href="/contact" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                    Contact Us
                  </Link>
                </li>
              </ul>
            </CollapsibleSection>
          </motion.div>

          {/* Desktop view - Products */}
          <motion.div variants={itemVariants} className="hidden md:block col-span-1">
            <CollapsibleSection title="Products">
              <ul className="space-y-3 md:space-y-2">
                {productGroups.flatMap(group => group.categories).slice(0, 6).map(category => (
                  <li key={category.slug}>
                    <Link href={`/products/${category.slug}`} className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
                      {category.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          </motion.div>

          {/* Desktop view - Legal */}
          <motion.div variants={itemVariants} className="hidden md:block col-span-1">
            <CollapsibleSection title="Legal" isLast={true}>
              <ul className="space-y-3 md:space-y-2">
                {legalPages.map(page => (
                  <li key={page.id}>
                    <Link href={`/${page.slug}`} className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all" prefetch={true}>
                      {page.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </CollapsibleSection>
          </motion.div>
        </motion.div>

        {/* Tablet view - links in same line */}
        <motion.div 
          className="hidden md:flex md:flex-wrap lg:hidden mt-6 justify-center gap-x-8 gap-y-2"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link href="/" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
            Home
          </Link>
          <Link href="/about" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
            About
          </Link>
          <Link href="/products" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
            Products
          </Link>
          <Link href="/industries" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
            Industries We Serve
          </Link>
          <Link href="/contact" className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all">
            Contact
          </Link>
          {legalPages.slice(0, 2).map(page => (
            <Link key={page.id} href={`/${page.slug}`} className="text-sm text-gray-600 hover:text-primary-600 dark:text-gray-300 dark:hover:text-primary-400 premium-transition-all" prefetch={true}>
              {page.slug === 'privacy-policy' ? 'Privacy' : page.slug === 'terms-of-service' ? 'Terms' : page.title}
            </Link>
          ))}
        </motion.div>

        {/* Copyright section - ensure visibility on all devices */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-2 md:mt-3 pb-2 pt-2 border-t border-gray-100 dark:border-gray-800/30">
          <div className="flex flex-col md:flex-row items-center justify-center min-h-[32px] text-xs text-gray-500 dark:text-gray-400 gap-1">
            <div className="flex items-center justify-center">
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </div>
            <div className="flex items-center justify-center gap-2 md:ml-4">
              <span>Theme:</span>
              <ThemeSwitcher size="sm" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
} 